import cn from 'clsx'
import {type FC, type MouseEvent} from 'react'
import {Link} from '@remix-run/react'
import {ArrowIcon} from '~/icons/home-page/arrow-icon'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import Spinner from '~/components/commons/spinner'

type TopListCardProps = {
  list: {
    id: string
    name: string
    image: string
    slug: string
  }
  quantityAnalyzed: string
  cardIndex: number
  category: string
  isLoading?: boolean
  useFourColumnLayout?: boolean
}

const TopListCard: FC<TopListCardProps> = ({
  list,
  quantityAnalyzed,
  cardIndex,
  category,
  isLoading = false,
  useFourColumnLayout = false,
}) => {
  const reportWidgetClicked = useReportWidgetClick()

  const [cardRef] = useReportWidgetSeen(
    {
      widget_name: 'improved-home-top-lists-card',
      widget_place: `${cardIndex + 1}`,
      trigger_type: category,
      variation: list.id,
    },
    {skip: isLoading},
  )

  const handleCardClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isLoading) {
      event.preventDefault()
      return
    }

    reportWidgetClicked({
      widget_name: 'improved-home-top-lists-card',
      widget_place: `${cardIndex + 1}`,
      trigger_type: category,
      variation: list.id,
    })
  }

  return (
    <Link
      ref={cardRef}
      prefetch={isLoading ? 'none' : 'intent'}
      onClick={handleCardClick}
      to={isLoading ? '#' : `/${list.slug}`}
      aria-disabled={isLoading}
      className={cn(
        'flex h-[288px] w-[245px] shrink-0 rounded-xl bg-white p-3 shadow-[0px_1.94px_9.68px_0px_#00000026] tablet:w-auto mobile:h-[203px] mobile:rounded-lg mobile:p-2',
        useFourColumnLayout && 'desktop:w-[calc((100%-120px)/4)]',
      )}
    >
      <div className="flex w-full flex-col gap-y-2 mobile:gap-y-0">
        <div className="flex h-[186px] w-full items-center justify-center rounded-[9px] bg-[#F2F2F4] p-3 mobile:h-[130px] mobile:rounded-md">
          {isLoading ? (
            <Spinner />
          ) : (
            <img
              src={list.image}
              alt={list.name}
              className="h-[176px] object-scale-down mix-blend-multiply mobile:h-[100px]"
            />
          )}
        </div>

        {isLoading ? (
          <span className="mt-1 h-4 animate-pulse rounded bg-[#F2F2F4]" />
        ) : (
          <span className="mt-1 h-4 text-xs font-light text-[#969696] mobile:text-[9px]">{quantityAnalyzed}</span>
        )}

        <div className="mt-auto flex h-10 justify-between gap-1 mobile:h-7">
          {isLoading ? (
            <div className="h-full w-3/4 animate-pulse rounded bg-[#F2F2F4] mobile:h-7" />
          ) : (
            <p className="mb-auto line-clamp-2 text-[19px] font-semibold leading-[1.1] text-[#181818] mobile:text-[13px] mobile:leading-3">
              {list.name}
            </p>
          )}
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-[#69C214] mobile:size-7 mobile:rounded-[4px]">
            <ArrowIcon className="shrink-0 desktop:size-4" />
          </span>
        </div>
      </div>
    </Link>
  )
}

export default TopListCard
